import { WorkspaceObjectRefBudget } from '@affine/core/modules/streamlain/copilot/workspace-view-budget';
import {
  createWorkflowReadCheckpoint,
  materializeWorkflowRead,
  type WorkflowReadOptions,
} from '@affine/core/modules/streamlain/workflow-viewer/workflow-context-read';
import { WorkspaceImpl } from '@affine/core/modules/workspace/impls/workspace';
import { prepareRequestJSON } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { getSurfaceBlock } from '@blocksuite/affine/blocks/surface';
import {
  DatabaseBlockModel,
  EmbedLinkedDocModel,
  EmbedSyncedDocModel,
  FrameBlockModel,
  GroupElementModel,
  ImageBlockModel,
  NoteBlockModel,
  NoteDisplayMode,
} from '@blocksuite/affine/model';
import {
  embedSyncedDocMiddleware,
  MarkdownAdapter,
  titleMiddleware,
} from '@blocksuite/affine/shared/adapters';
import {
  getImageSelectionsCommand,
  getSelectedBlocksCommand,
} from '@blocksuite/affine/shared/commands';
import {
  DocModeProvider,
  UserProvider,
} from '@blocksuite/affine/shared/services';
import {
  getBlockPropsWork,
  isInsideEdgelessEditor,
  matchModels,
} from '@blocksuite/affine/shared/utils';
import { BlockStdScope, type EditorHost } from '@blocksuite/affine/std';
import {
  GfxControllerIdentifier,
  type GfxModel,
  GfxPrimitiveElementModel,
} from '@blocksuite/affine/std/gfx';
import type { BlockModel, DocSnapshot, Store } from '@blocksuite/affine/store';
import { Slice, toDraftModel } from '@blocksuite/affine/store';
import { getElementPropsWork } from '@blocksuite/affine-block-root';
import {
  WORKSPACE_VIEW_MAX_BYTES,
  WORKSPACE_VIEW_MAX_REF_VISITS,
  type WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import {
  FILE_INDEX_MANAGED_BY,
  isFileIndexDatabaseId,
  PAGE_PART_DIRECTORY_ORIGIN,
} from '@streamlain/database-identity';
import { Doc as YDoc, type Transaction } from 'yjs';

import { getStoreManager } from '../../manager/store';
import type { ChatContextValue } from '../components/ai-chat-content';
import { ChatContextReadError } from '../components/ai-chat-content/chat-context-manager';
import type {
  CanvasAIChatContextMode,
  CanvasSelectionItem,
  ChatContextSelection,
} from '../components/ai-chat-content/type';
// Import and re-export from screenshot service for backwards compatibility
import {
  getScreenshotService,
  type IScreenshotService,
  type ScreenshotProgressHandler,
  ScreenshotService,
  type ScreenshotSizeCategory,
  type ScreenshotSizeValidation,
  ScreenshotTooLargeError,
} from '../services/screenshot-service';
import {
  getAIChatSelectionContext,
  getAIChatSelectionContextWork,
} from './ai-chat-selection-context';
import { isAttachment } from './attachment';
import { finiteBoundsFromXYWH } from './canvas-bounds';
import { flattenCanvasSelectionItems } from './canvas-selection-areas';
import { buildCanvasSelectionContextFromItems } from './canvas-selection-context-builder';
import {
  canvasSelectionRoleLabel,
  canvasSelectionTypeLabel,
} from './canvas-selection-display';
import {
  getPinnedTextContentsFromBlockModels,
  getSelectedAttachments,
  getSelectedImagesAsBlobs,
  getSelectedTextContent,
} from './selection-utils';

/** Revision and resource lifetime belong to one native context read. */
export async function withNativeContextRead<T>(
  host: EditorHost,
  options: WorkflowReadOptions,
  read: (assertCurrent: () => void) => Promise<T>
): Promise<T> {
  const store = host.store;
  const std = host.std;
  const spaceDoc = store.spaceDoc;
  const lifetime = store.disposableGroup;
  let changedDuringRead = false;
  // Every loaded part of the page counts; part zero is the space doc with the
  // head. The part directory only keeps the bookkeeping of the parts
  // (measured bytes, bounds), written later than the edit; it is no new
  // revision.
  const afterTransaction = (transaction: Transaction) => {
    if (
      transaction.origin !== PAGE_PART_DIRECTORY_ORIGIN &&
      transaction.changed.size
    )
      changedDuringRead = true;
  };
  const assertCurrent = () => {
    options.signal.throwIfAborted();
    options.assertCurrent();
    if (
      changedDuringRead ||
      host.store !== store ||
      host.std !== std ||
      store.spaceDoc !== spaceDoc ||
      store.disposableGroup !== lifetime ||
      lifetime.disposed
    )
      throw new ChatContextReadError('stale');
  };
  assertCurrent();
  const releaseParts = store.pageStructure.onPartTransaction(afterTransaction);
  try {
    const result = await read(assertCurrent);
    assertCurrent();
    return result;
  } finally {
    releaseParts();
  }
}

/** Capture native identities only. Bodies remain on the existing Store/Gfx owner. */
export function captureNativeContextSelection(options: {
  host: EditorHost;
  getHost: () => EditorHost | undefined;
  scope: WorkspaceViewScope;
  kind: 'doc' | 'canvas';
  ids: readonly string[];
  assertCurrent: () => void;
  includeDetailedObject?: () => boolean;
  canvasAIChatContextMode?: CanvasAIChatContextMode;
}): Extract<ChatContextSelection, { kind: 'native' }> {
  const { host, kind } = options;
  if (!options.ids.length) throw new ChatContextReadError('unavailable');
  if (options.ids.length > WORKSPACE_VIEW_MAX_REF_VISITS)
    throw new ChatContextReadError('oversized');
  for (const id of options.ids) {
    if (typeof id !== 'string' || !id)
      throw new ChatContextReadError('invalid');
    if (id.length > WORKSPACE_VIEW_MAX_BYTES)
      throw new ChatContextReadError('oversized');
  }
  const store = host.store,
    std = host.std,
    spaceDoc = store.spaceDoc;
  const lifetime = store.disposableGroup;
  if (store.workspace.id !== options.scope.workspaceId)
    throw new ChatContextReadError('stale');
  const budget = new WorkspaceObjectRefBudget({ kind, id: store.id });
  for (const id of options.ids) {
    if (
      !budget.visit() ||
      !budget.add({
        kind,
        id,
        attributes: {
          workspaceId: options.scope.workspaceId,
          viewId: options.scope.viewId,
        },
      })
    )
      throw new ChatContextReadError('oversized');
  }
  const gfx = kind === 'canvas' ? std.get(GfxControllerIdentifier) : undefined;
  const lookup = (id: string) =>
    gfx ? gfx.getElementById(id) : store.getModelById(id);
  const ids = [...new Set(options.ids)];
  const capturedModels = new Map(ids.map(id => [id, lookup(id)]));
  const assertCurrent = () => {
    options.assertCurrent();
    if (
      options.getHost() !== host ||
      host.store !== store ||
      host.std !== std ||
      store.spaceDoc !== spaceDoc ||
      store.disposableGroup !== lifetime ||
      lifetime.disposed
    )
      throw new ChatContextReadError('stale');
    for (const [id, model] of capturedModels) {
      if (!model) throw new ChatContextReadError('unavailable');
      if (lookup(id) !== model) throw new ChatContextReadError('stale');
    }
  };
  assertCurrent();
  const scope = { ...options.scope };
  const generation = spaceDoc.guid;
  return {
    kind: 'native',
    provenance: 'explicit',
    scope,
    key: JSON.stringify([
      scope.workspaceId,
      scope.viewId,
      kind,
      store.id,
      generation,
      ids,
    ]),
    sourceKey: `native-${kind}:${store.id}`,
    sourceGeneration: generation,
    label: I18n.t('com.streamlain.ai.context.canvas.object-many', {
      count: ids.length,
    }),
    binding: {
      kind,
      workspaceId: store.workspace.id,
      docId: store.id,
      storeGeneration: generation,
      objectIds: ids,
    },
    assertCurrent,
    materialize: async (signal, unionIds = ids, assertSendCurrent) => {
      return withNativeContextRead(
        host,
        {
          signal,
          assertCurrent: () => {
            assertCurrent();
            assertSendCurrent?.();
          },
        },
        async assertReadCurrent => {
          const check = () => {
            signal.throwIfAborted();
            assertReadCurrent();
          };
          const selectedIds = new Set(unionIds);
          const work = function* () {
            const models: GfxModel[] = [];
            const blocks: BlockModel[] = [];
            const items: CanvasSelectionItem[] = [];
            const visited = new Set<string>();
            for (const id of selectedIds) {
              yield;
              const model = lookup(id);
              if (!model) throw new ChatContextReadError('unavailable');
              if (gfx) {
                models.push(model as GfxModel);
                if (
                  options.canvasAIChatContextMode === 'lineage-only' &&
                  getCanvasSelectionType(model) === 'affine:embed-ai-chat'
                )
                  continue;
                const item = yield* buildCanvasSelectionItemWork(
                  host,
                  model,
                  selectedIds,
                  visited
                );
                if (item) items.push(item);
              } else blocks.push(model as BlockModel);
            }
            return { models, blocks, items };
          };
          const { models, blocks, items } = await materializeWorkflowRead(
            work(),
            {
              signal,
              assertCurrent: assertReadCurrent,
            }
          );
          check();
          if (gfx) {
            const context = items.length
              ? buildCanvasSelectionContextFromItems({
                  docId: store.id,
                  label: getCanvasSelectionContextLabel(items),
                  items,
                  source: 'focus',
                  priority: 'highest',
                })
              : null;
            const result = await extractEdgelessSelectedContent(
              host,
              undefined,
              options.includeDetailedObject?.() ?? true,
              options.canvasAIChatContextMode ?? 'selection-analysis',
              true,
              { elements: models, ids: selectedIds, context },
              undefined,
              { signal, assertCurrent: assertReadCurrent }
            );
            check();
            return result ?? {};
          }
          const text = await getPinnedTextContentsFromBlockModels(
            host,
            blocks,
            {
              signal,
              assertCurrent: assertReadCurrent,
            }
          );
          check();
          const images: File[] = [],
            attachments: ChatContextValue['attachments'] = [],
            docs: string[] = [];
          for (const model of blocks) {
            check();
            if (model instanceof ImageBlockModel && model.props.sourceId) {
              const blob = await store.blobSync.get(model.props.sourceId);
              check();
              if (!blob) throw new ChatContextReadError('unavailable');
              images.push(
                new File([blob], model.props.sourceId, { type: blob.type })
              );
            } else if (isAttachment(model)) {
              const { name, sourceId } = model.props;
              if (name && sourceId) attachments.push({ name, sourceId });
            } else if (
              model instanceof EmbedSyncedDocModel ||
              model instanceof EmbedLinkedDocModel
            )
              docs.push(model.props.pageId);
          }
          return { ...text, images, attachments, docs };
        }
      );
    },
  };
}

function isHiddenFileIndexSelection(element: unknown): boolean {
  const candidate = element as {
    id?: unknown;
    props?: Record<string, unknown>;
  };
  const id = typeof candidate.id === 'string' ? candidate.id : undefined;
  const managedBy = candidate.props?.managedBy;
  const visible = candidate.props?.visible;
  const isFileIndex =
    managedBy === FILE_INDEX_MANAGED_BY || isFileIndexDatabaseId(id);

  return isFileIndex && visible !== true;
}

// Re-export for backwards compatibility
export {
  getScreenshotService,
  type IScreenshotService,
  type ScreenshotProgressHandler,
  ScreenshotService,
  type ScreenshotSizeCategory,
  type ScreenshotSizeValidation,
  ScreenshotTooLargeError,
};

export async function extractSelectedContent(
  host: EditorHost,
  progressHandler?: ScreenshotProgressHandler,
  options?: {
    includeDetailedObject?: boolean;
    canvasAIChatContextMode?: CanvasAIChatContextMode;
    /** Omit metadata when this send already captured the native selection. */
    includeCanvasSelectionContext?: boolean;
    /** Already materialized by a bound native pin in this send. */
    excludedCanvasIds?: ReadonlySet<string>;
    /** Actual Send owns the signal and revision assertion for native bodies. */
    nativeRead?: WorkflowReadOptions;
  }
): Promise<Partial<ChatContextValue> | null> {
  const docModeService = host.std.get(DocModeProvider);
  const mode = docModeService.getEditorMode() || 'page';
  if (mode === 'edgeless') {
    return await extractEdgelessSelected(
      host,
      progressHandler,
      options?.includeDetailedObject ?? true,
      options?.canvasAIChatContextMode ?? 'selection-analysis',
      options?.includeCanvasSelectionContext ?? true,
      undefined,
      options?.excludedCanvasIds,
      options?.nativeRead
    );
  } else {
    return await extractPageSelected(host);
  }
}

function asReadableText(value: unknown) {
  if (typeof value === 'string') {
    const text = value.trim();
    return text.length ? text : null;
  }
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { toString?: unknown }).toString === 'function'
  ) {
    const text = (value as { toString: () => string }).toString().trim();
    if (text && text !== '[object Object]') return text;
  }
  return null;
}

function getCanvasSelectionDirectText(element: unknown) {
  const candidate = element as {
    text?: unknown;
    props?: Record<string, unknown>;
    title?: unknown;
    name?: unknown;
  };
  return (
    asReadableText(candidate.text) ||
    asReadableText(candidate.props?.text) ||
    asReadableText(candidate.props?.title) ||
    asReadableText(candidate.props?.name) ||
    asReadableText(candidate.props?.caption) ||
    asReadableText(candidate.title) ||
    asReadableText(candidate.name)
  );
}

/** Same native preview traversal, with a work boundary before every child. */
function* getCanvasSelectionDescendantTextWork(
  element: unknown,
  visited = new Set<string>()
): Generator<void, string | null> {
  type Frame = {
    children: Iterator<unknown>;
    parts: string[];
    length: number;
  };
  const frames: Frame[] = [];
  const enter = function* (value: unknown): Generator<void, boolean> {
    yield;
    const candidate = value as { id?: unknown; children?: unknown };
    if (typeof candidate.id === 'string') {
      if (visited.has(candidate.id)) return false;
      visited.add(candidate.id);
    }
    const children = candidate.children;
    const iterable: Iterable<unknown> = Array.isArray(children)
      ? children
      : children &&
          typeof children === 'object' &&
          typeof (children as { values?: unknown }).values === 'function'
        ? {
            [Symbol.iterator]: () =>
              (children as { values(): Iterator<unknown> }).values(),
          }
        : [];
    frames.push({
      children: iterable[Symbol.iterator](),
      parts: [],
      length: 0,
    });
    return true;
  };
  const append = (frame: Frame, text: string) => {
    frame.length += text.length + (frame.parts.length ? 1 : 0);
    frame.parts.push(text);
  };
  yield* enter(element);
  while (frames.length) {
    const frame = frames[frames.length - 1];
    yield;
    const next = frame.children.next();
    if (!next.done) {
      const direct = getCanvasSelectionDirectText(next.value);
      if (direct) append(frame, direct);
      if (yield* enter(next.value)) continue;
      if (frame.length <= 240) continue;
    }
    const text = frame.parts.join(' · ').trim();
    frames.pop();
    if (!frames.length) return text || null;
    const parent = frames[frames.length - 1];
    if (text) append(parent, text);
    // The original bound is applied after both direct and nested child text.
    // Completing the parent here preserves its exact joined preview.
    while (frames.length && frames[frames.length - 1].length > 240) {
      const current = frames.pop()!;
      const nested = current.parts.join(' · ').trim();
      if (!frames.length) return nested || null;
      if (nested) append(frames[frames.length - 1], nested);
    }
  }
  return null;
}

function formatCanvasSelectionSize(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / 1024 / 102.4) / 10} MB`;
}

function getDatabaseTitle(element: unknown): string | null {
  const candidate = element as {
    props?: {
      title?: unknown;
    };
  };
  return asReadableText(candidate.props?.title);
}

function* getDatabasePreviewTextWork(
  element: unknown
): Generator<void, string | null> {
  yield;
  const candidate = element as {
    props?: {
      columns?: unknown;
      views?: unknown;
      activeViewId?: unknown;
    };
  };
  const columns = Array.isArray(candidate.props?.columns)
    ? candidate.props.columns.length
    : null;
  const views = Array.isArray(candidate.props?.views)
    ? candidate.props.views
    : [];
  const activeViewId = candidate.props?.activeViewId;
  let activeView: unknown =
    typeof activeViewId === 'string' ? undefined : views[0];
  if (typeof activeViewId === 'string') {
    for (const view of views) {
      yield;
      if (
        view &&
        typeof view === 'object' &&
        (view as { id?: unknown }).id === activeViewId
      ) {
        activeView = view;
        break;
      }
    }
  }
  const activeViewName =
    activeView && typeof activeView === 'object'
      ? asReadableText((activeView as { name?: unknown; mode?: unknown }).name)
      : null;
  const activeViewMode =
    activeView && typeof activeView === 'object'
      ? asReadableText((activeView as { mode?: unknown }).mode)
      : null;
  const parts = [
    columns === null
      ? null
      : I18n.t('com.streamlain.ai.context.columns', { count: columns }),
    activeViewName ?? activeViewMode,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function getImagePreviewText(element: unknown, label: string): string | null {
  const candidate = element as {
    props?: {
      caption?: unknown;
      width?: unknown;
      height?: unknown;
      size?: unknown;
    };
  };
  const caption = asReadableText(candidate.props?.caption);
  const dimensions =
    typeof candidate.props?.width === 'number' &&
    typeof candidate.props?.height === 'number' &&
    candidate.props.width > 0 &&
    candidate.props.height > 0
      ? `${Math.round(candidate.props.width)} x ${Math.round(
          candidate.props.height
        )} px`
      : null;
  const size = formatCanvasSelectionSize(candidate.props?.size);
  const parts = [
    caption && caption !== label ? caption : null,
    dimensions,
    size,
  ]
    .filter(Boolean)
    .slice(0, 2);
  return parts.length ? parts.join(' · ') : null;
}

function getAttachmentLabel(element: unknown): string | null {
  const candidate = element as {
    props?: {
      name?: unknown;
      caption?: unknown;
    };
  };
  return (
    asReadableText(candidate.props?.name) ??
    asReadableText(candidate.props?.caption)
  );
}

function getAttachmentPreviewText(element: unknown, label: string) {
  const candidate = element as {
    props?: {
      caption?: unknown;
      type?: unknown;
      size?: unknown;
    };
  };
  const caption = asReadableText(candidate.props?.caption);
  const type = asReadableText(candidate.props?.type);
  const size = formatCanvasSelectionSize(candidate.props?.size);
  const parts = [caption && caption !== label ? caption : null, type, size]
    .filter(Boolean)
    .slice(0, 2);
  return parts.length ? parts.join(' · ') : null;
}

function getCanvasSelectionType(element: unknown) {
  const candidate = element as {
    type?: unknown;
    flavour?: unknown;
  };
  if (typeof candidate.type === 'string') return candidate.type;
  if (typeof candidate.flavour === 'string') return candidate.flavour;
  return 'canvas-object';
}

function getCanvasSelectionRole(
  element: unknown
): NonNullable<CanvasSelectionItem['role']> {
  const candidate = element as {
    type?: unknown;
    flavour?: unknown;
    props?: { type?: unknown };
  };
  if (element instanceof GroupElementModel || candidate.type === 'group') {
    return 'group';
  }
  if (
    element instanceof FrameBlockModel ||
    candidate.flavour === 'affine:frame'
  ) {
    return 'frame';
  }
  if (
    element instanceof DatabaseBlockModel ||
    candidate.flavour === 'affine:database'
  ) {
    return 'database';
  }
  if (isAttachment(element as Parameters<typeof isAttachment>[0])) {
    return candidate.props?.type === 'application/pdf' ? 'pdf' : 'attachment';
  }
  if (candidate.type === 'mindmap') return 'mindmap';
  return 'object';
}

function getCanvasSelectionLabel(
  element: unknown,
  role: NonNullable<CanvasSelectionItem['role']>,
  type: string,
  readDirectText: () => string | null,
  aiChatContext: ReturnType<typeof getAIChatSelectionContext> | null
) {
  const candidate = element as {
    props?: Record<string, unknown> & { name?: unknown; title?: unknown };
    title?: unknown;
    name?: unknown;
  };
  if (aiChatContext) {
    const baseLabel =
      canvasSelectionTypeLabel(type, role) ?? canvasSelectionRoleLabel(role);
    const { labelSuffix } = aiChatContext;
    return labelSuffix ? `${baseLabel}: ${labelSuffix}` : baseLabel;
  }
  if (element instanceof NoteBlockModel || type === 'affine:note') {
    return (
      canvasSelectionTypeLabel(type, role) ?? canvasSelectionRoleLabel(role)
    );
  }
  if (
    element instanceof DatabaseBlockModel ||
    role === 'database' ||
    type === 'affine:database'
  ) {
    return (
      getDatabaseTitle(element) ??
      canvasSelectionTypeLabel(type, role) ??
      canvasSelectionRoleLabel(role)
    );
  }
  if (
    isAttachment(element as Parameters<typeof isAttachment>[0]) ||
    role === 'pdf' ||
    role === 'attachment' ||
    type === 'affine:attachment'
  ) {
    return (
      getAttachmentLabel(element) ??
      canvasSelectionTypeLabel(type, role) ??
      canvasSelectionRoleLabel(role)
    );
  }
  const text =
    readDirectText() ||
    asReadableText(candidate.props?.name) ||
    asReadableText(candidate.props?.title) ||
    asReadableText(candidate.title) ||
    asReadableText(candidate.name);
  if (text) return text;
  const typeLabel = canvasSelectionTypeLabel(type, role);
  if (typeLabel) return typeLabel;
  return canvasSelectionRoleLabel(role);
}

function* getCanvasSelectionPreviewTextWork(
  element: unknown,
  role: NonNullable<CanvasSelectionItem['role']>,
  type: string,
  label: string,
  readDirectText: () => string | null,
  aiChatContext: ReturnType<typeof getAIChatSelectionContext> | null
) {
  if (aiChatContext) {
    return aiChatContext.transcript;
  }
  if (element instanceof NoteBlockModel || type === 'affine:note') {
    return yield* getCanvasSelectionDescendantTextWork(element);
  }
  if (
    element instanceof DatabaseBlockModel ||
    role === 'database' ||
    type === 'affine:database'
  ) {
    return yield* getDatabasePreviewTextWork(element);
  }
  if (element instanceof ImageBlockModel || type === 'affine:image') {
    return getImagePreviewText(element, label);
  }
  if (type === 'affine:sticky-note') {
    const candidate = element as {
      props?: { text?: unknown; assigneeId?: unknown };
    };
    const stickyText = asReadableText(candidate.props?.text) || label;
    const assigneeId = asReadableText(candidate.props?.assigneeId);
    return assigneeId
      ? `${stickyText}\nAssignee ID: ${assigneeId}`
      : stickyText;
  }
  if (type === 'affine:brush-heading') {
    const candidate = element as { props?: { text?: unknown } };
    return asReadableText(candidate.props?.text) || label;
  }
  if (
    isAttachment(element as Parameters<typeof isAttachment>[0]) ||
    role === 'pdf' ||
    role === 'attachment' ||
    type === 'affine:attachment'
  ) {
    return getAttachmentPreviewText(element, label);
  }

  const direct = readDirectText();
  if (direct && direct !== label) return direct;
  return canvasSelectionTypeLabel(type, role);
}

function* buildCanvasSelectionItemWork(
  host: EditorHost,
  element: unknown,
  selectedIds: ReadonlySet<string>,
  visited: Set<string>
): Generator<void, CanvasSelectionItem | null> {
  yield;
  const candidate = element as {
    id?: unknown;
    flavour?: unknown;
    group?: { id?: unknown } | null;
    props?: Record<string, unknown>;
    xywh?: unknown;
  };
  if (typeof candidate.id !== 'string' || !candidate.id) return null;
  if (isHiddenFileIndexSelection(element)) return null;
  if (visited.has(candidate.id)) return null;
  visited.add(candidate.id);

  const type = getCanvasSelectionType(element);
  const role = getCanvasSelectionRole(element);
  const aiChatContext =
    type === 'affine:embed-ai-chat'
      ? yield* getAIChatSelectionContextWork(candidate.props)
      : null;
  // Label and preview describe the same native selection read. Materialize
  // direct text at most once, and only for roles that actually need it.
  let directText: string | null | undefined;
  const readDirectText = (): string | null => {
    if (directText === undefined)
      directText = getCanvasSelectionDirectText(element);
    return directText;
  };
  const label = getCanvasSelectionLabel(
    element,
    role,
    type,
    readDirectText,
    aiChatContext
  );
  let text = yield* getCanvasSelectionPreviewTextWork(
    element,
    role,
    type,
    label,
    readDirectText,
    aiChatContext
  );
  if (type === 'affine:sticky-note') {
    const assigneeId = asReadableText(candidate.props?.assigneeId);
    if (assigneeId) {
      const assignee = host.std
        .getOptional(UserProvider)
        ?.userInfo$(assigneeId).value;
      if (assignee && !assignee.removed && assignee.name) {
        text = `${asReadableText(candidate.props?.text) || label}\nAssignee: ${assignee.name}\nAssignee ID: ${assigneeId}`;
      }
    }
  }
  const bounds = finiteBoundsFromXYWH(candidate.xywh ?? candidate.props?.xywh);
  const kind =
    typeof candidate.flavour === 'string' ? 'block' : 'canvas_element';
  const groupId =
    typeof candidate.group?.id === 'string' ? candidate.group.id : null;
  const parentId = groupId && selectedIds.has(groupId) ? groupId : null;

  return {
    id: candidate.id,
    kind,
    type,
    role,
    label,
    text,
    bounds,
    parentId,
    conversationParentId: aiChatContext?.conversationParentId ?? undefined,
    depth: parentId ? 1 : 0,
    childCount: 0,
    groupId,
  };
}

function buildCanvasSelectionItem(
  host: EditorHost,
  element: unknown,
  selectedIds: ReadonlySet<string>,
  visited: Set<string>
): CanvasSelectionItem | null {
  const work = buildCanvasSelectionItemWork(
    host,
    element,
    selectedIds,
    visited
  );
  let step = work.next();
  while (!step.done) step = work.next();
  return step.value;
}

function normalizeCanvasSelectionItems(
  items: CanvasSelectionItem[]
): CanvasSelectionItem[] {
  const allItems: CanvasSelectionItem[] = [];
  const inheritedParentById = new Map<string, string | null>();
  const collect = (
    selectionItems: CanvasSelectionItem[],
    inheritedParentId: string | null
  ) => {
    for (const item of selectionItems) {
      allItems.push(item);
      inheritedParentById.set(item.id, inheritedParentId);
      if (item.children?.length) collect(item.children, item.id);
    }
  };
  collect(items, null);

  const selectedIds = new Set(allItems.map(item => item.id));
  const parentById = new Map(
    allItems.map(item => {
      const declaredParent = item.parentId;
      return [
        item.id,
        declaredParent && selectedIds.has(declaredParent)
          ? declaredParent
          : (inheritedParentById.get(item.id) ?? null),
      ] as const;
    })
  );
  const childCountById = new Map<string, number>();
  for (const parentId of parentById.values()) {
    if (!parentId) continue;
    childCountById.set(parentId, (childCountById.get(parentId) ?? 0) + 1);
  }
  const depthFor = (itemId: string) => {
    let depth = 0;
    let parentId = parentById.get(itemId) ?? null;
    const seen = new Set([itemId]);
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      depth++;
      parentId = parentById.get(parentId) ?? null;
    }
    return depth;
  };
  const normalize = (item: CanvasSelectionItem): CanvasSelectionItem => {
    const children = item.children?.map(normalize);
    return {
      ...item,
      parentId: parentById.get(item.id) ?? null,
      depth: depthFor(item.id),
      childCount: childCountById.get(item.id) ?? 0,
      children: children?.length ? children : undefined,
    };
  };
  return items.map(normalize);
}

function removeCoveredCanvasSelectionItems(
  items: CanvasSelectionItem[],
  coveredBlockIds: ReadonlySet<string>,
  aiChatContextMode: CanvasAIChatContextMode
): CanvasSelectionItem[] {
  const filtered: CanvasSelectionItem[] = [];

  for (const item of items) {
    // AI chat blocks are conversation lineage, not document context. Keeping
    // them here duplicates the transcript, shows a misleading "AI chat" pill,
    // and can feed the same conversation back into the model as canvas data.
    if (
      item.type === 'affine:embed-ai-chat' &&
      (aiChatContextMode === 'lineage-only' || !item.text)
    ) {
      continue;
    }
    if (item.kind === 'block' && coveredBlockIds.has(item.id)) {
      continue;
    }

    const children = item.children?.length
      ? removeCoveredCanvasSelectionItems(
          item.children,
          coveredBlockIds,
          aiChatContextMode
        )
      : undefined;

    filtered.push({
      ...item,
      children: children?.length ? children : undefined,
      childCount: children?.length ?? 0,
    });
  }

  return normalizeCanvasSelectionItems(filtered);
}

function getCanvasSelectionContextLabel(items: CanvasSelectionItem[]) {
  if (items.length === 1) {
    return items[0].label;
  }

  return I18n.t('com.streamlain.ai.context.canvas.object-many', {
    count: items.length,
  });
}

export function filterCanvasSelectionsByCoveredBlockIds(
  selections: ChatContextValue['canvasSelections'],
  coveredBlockIds: Iterable<string>,
  aiChatContextMode: CanvasAIChatContextMode
): ChatContextValue['canvasSelections'] {
  const covered = new Set([...coveredBlockIds].filter(Boolean));

  return selections
    .map(selection => {
      const items = removeCoveredCanvasSelectionItems(
        selection.items,
        covered,
        aiChatContextMode
      );
      const flatItems = flattenCanvasSelectionItems(items);
      return {
        ...selection,
        items,
        elementIds: flatItems
          .filter(item => item.kind === 'canvas_element')
          .map(item => item.id),
        blockIds: flatItems
          .filter(item => item.kind === 'block')
          .map(item => item.id),
        label: getCanvasSelectionContextLabel(items),
      };
    })
    .filter(selection => selection.items.length > 0);
}

function buildCanvasSelectionContext(
  host: EditorHost,
  selectedElements: Iterable<unknown>,
  selectedIds: ReadonlySet<string>,
  aiChatContextMode: CanvasAIChatContextMode
): ChatContextValue['canvasSelections'][number] | null {
  const items: CanvasSelectionItem[] = [];
  const visited = new Set<string>();
  for (const element of selectedElements) {
    // This native block has no children and is discarded by the same mode's
    // downstream filter. Reject it before reading/parsing its transcript.
    if (
      aiChatContextMode === 'lineage-only' &&
      getCanvasSelectionType(element) === 'affine:embed-ai-chat'
    ) {
      continue;
    }
    const item = buildCanvasSelectionItem(host, element, selectedIds, visited);
    if (item) items.push(item);
  }

  if (!items.length) return null;
  return buildCanvasSelectionContextFromItems({
    docId: host.store.id,
    label: getCanvasSelectionContextLabel(items),
    items,
    source: 'focus',
    priority: 'highest',
  });
}

export function extractCanvasSelectionContext(
  host: EditorHost,
  aiChatContextMode: CanvasAIChatContextMode = 'selection-analysis',
  excludedIds: ReadonlySet<string> = new Set()
): ChatContextValue['canvasSelections'] {
  if (!isInsideEdgelessEditor(host)) return [];
  const gfx = host.std.getOptional(GfxControllerIdentifier);
  if (!gfx) return [];

  // Consume the native selection membership directly. selectedElements also
  // copies selectedIds, then allocates a second array of all selected models.
  const selectedIds = gfx.selection.selectedSet;
  const selectedElements = function* () {
    for (const id of selectedIds) {
      if (excludedIds.has(id)) continue;
      const element = gfx.getElementById(id);
      if (element) yield element;
    }
  };
  const canvasSelection = buildCanvasSelectionContext(
    host,
    selectedElements(),
    selectedIds,
    aiChatContextMode
  );
  return canvasSelection ? [canvasSelection] : [];
}

/** The actual Send consumes the same native projection through its owned driver. */
export async function extractCanvasSelectionContextAsync(
  host: EditorHost,
  aiChatContextMode: CanvasAIChatContextMode,
  excludedIds: ReadonlySet<string>,
  options: WorkflowReadOptions
): Promise<ChatContextValue['canvasSelections']> {
  if (!isInsideEdgelessEditor(host)) return [];
  const gfx = host.std.getOptional(GfxControllerIdentifier);
  if (!gfx || !gfx.selection.selectedSet.size) return [];
  const selectedIds = gfx.selection.selectedSet;
  return withNativeContextRead(host, options, async assertNativeCurrent => {
    const assertCurrent = () => {
      assertNativeCurrent();
      if (
        host.std.getOptional(GfxControllerIdentifier) !== gfx ||
        gfx.selection.selectedSet !== selectedIds
      )
        throw new ChatContextReadError('stale');
    };
    const work = function* (): Generator<
      void,
      ChatContextValue['canvasSelections']
    > {
      const items: CanvasSelectionItem[] = [];
      const visited = new Set<string>();
      for (const id of selectedIds) {
        yield;
        if (excludedIds.has(id)) continue;
        const element = gfx.getElementById(id);
        if (!element) continue;
        if (
          aiChatContextMode === 'lineage-only' &&
          getCanvasSelectionType(element) === 'affine:embed-ai-chat'
        )
          continue;
        const item = yield* buildCanvasSelectionItemWork(
          host,
          element,
          selectedIds,
          visited
        );
        if (item) items.push(item);
      }
      if (!items.length) return [];
      const selection = buildCanvasSelectionContextFromItems({
        docId: host.store.id,
        label: getCanvasSelectionContextLabel(items),
        items,
        source: 'focus',
        priority: 'highest',
      });
      return selection ? [selection] : [];
    };
    return materializeWorkflowRead(work(), {
      signal: options.signal,
      assertCurrent,
    });
  });
}

async function extractEdgelessSelected(
  ...args: Parameters<typeof extractEdgelessSelectedContent>
): Promise<Partial<ChatContextValue> | null> {
  const [host, , , , , bound, excludedIds, options] = args;
  if (!options || bound || !isInsideEdgelessEditor(host))
    return extractEdgelessSelectedContent(...args);
  const gfx = host.std.get(GfxControllerIdentifier);
  const ids = gfx.selection.selectedSet;
  // A pin already materialized every selected ID; do not acquire a second read.
  const assertSelectionCurrent = () => {
    options.signal.throwIfAborted();
    options.assertCurrent();
    if (
      gfx.selection.selectedSet !== ids ||
      host.std.getOptional(GfxControllerIdentifier) !== gfx
    )
      throw new ChatContextReadError('stale');
  };
  const covered = await materializeWorkflowRead(
    (function* (): Generator<void, boolean> {
      for (const id of ids) {
        yield;
        if (!excludedIds?.has(id)) return false;
      }
      return true;
    })(),
    { signal: options.signal, assertCurrent: assertSelectionCurrent }
  );
  if (covered) return extractEdgelessSelectedContent(...args);
  return withNativeContextRead(host, options, assertNativeCurrent => {
    const assertCurrent = () => {
      assertNativeCurrent();
      if (
        gfx.selection.selectedSet !== ids ||
        host.std.getOptional(GfxControllerIdentifier) !== gfx
      )
        throw new ChatContextReadError('stale');
    };
    args[7] = { signal: options.signal, assertCurrent };
    return extractEdgelessSelectedContent(...args);
  });
}

async function extractEdgelessSelectedContent(
  host: EditorHost,
  progressHandler?: ScreenshotProgressHandler,
  includeDetailedObject = true,
  aiChatContextMode: CanvasAIChatContextMode = 'selection-analysis',
  includeCanvasSelectionContext = true,
  bound?: {
    elements: GfxModel[];
    ids: ReadonlySet<string>;
    context: ChatContextValue['canvasSelections'][number] | null;
  },
  excludedIds?: ReadonlySet<string>,
  readOptions?: WorkflowReadOptions
): Promise<Partial<ChatContextValue> | null> {
  if (!isInsideEdgelessEditor(host)) return null;
  const gfx = host.std.get(GfxControllerIdentifier);
  const consume = async <T>(work: Generator<void, T>): Promise<T> => {
    if (readOptions) return materializeWorkflowRead(work, readOptions);
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  };
  const checkpoint = readOptions
    ? createWorkflowReadCheckpoint(readOptions)
    : undefined;
  const check = () => {
    readOptions?.signal.throwIfAborted();
    readOptions?.assertCurrent();
  };
  check();
  const selectedElements =
    bound?.elements ??
    (await consume(
      (function* (): Generator<void, GfxModel[]> {
        const models: GfxModel[] = [];
        for (const id of gfx.selection.selectedSet) {
          yield;
          if (excludedIds?.has(id)) continue;
          const element = gfx.getElementById<GfxModel>(id);
          if (element) models.push(element);
        }
        return models;
      })()
    ));
  const canvasSelection = bound
    ? bound.context
    : includeCanvasSelectionContext
      ? buildCanvasSelectionContext(
          host,
          selectedElements,
          gfx.selection.selectedSet,
          aiChatContextMode
        )
      : null;

  // Keep the hard bound guard, but do not rasterize canvas selections for chat.
  // The structured canvasSelections payload already carries IDs and labels for
  // follow-up tools without blocking the main thread on html2canvas.
  const screenshotService = getScreenshotService();
  if (progressHandler) {
    screenshotService.setProgressHandler(progressHandler);
  }

  const sizeValidation = screenshotService.validateSize(selectedElements);

  if (sizeValidation.category === 'denied') {
    return canvasSelection
      ? {
          canvasSelections: [canvasSelection],
        }
      : null;
  }

  let snapshot: DocSnapshot | null = null;
  let markdown = '';
  const attachments: ChatContextValue['attachments'] = [];
  const docs: ChatContextValue['docs'] = [];

  // Chat can transmit native selection metadata without the optional object
  // body. Keep files/doc references, but avoid building a discarded fragment.
  if (!includeDetailedObject) {
    await consume(
      (function* (): Generator<void, void> {
        for (const element of selectedElements) {
          yield;
          if (isAttachment(element)) {
            const { name, sourceId } = element.props;
            if (name && sourceId) attachments.push({ name, sourceId });
          } else if (
            element instanceof EmbedSyncedDocModel ||
            element instanceof EmbedLinkedDocModel
          ) {
            docs.push(element.props.pageId);
          }
        }
      })()
    );
    check();
    return {
      snapshot: null,
      combinedElementsMarkdown: null,
      attachments,
      docs,
      canvasSelections: canvasSelection ? [canvasSelection] : [],
    };
  }

  if (selectedElements.length) {
    const transformer = host.store.getTransformer();
    const markdownAdapter = new MarkdownAdapter(
      transformer,
      host.store.provider
    );
    const collection = new WorkspaceImpl({
      id: 'AI_EXTRACT',
      rootDoc: new YDoc({ guid: 'AI_EXTRACT' }),
    });
    collection.meta.initialize();

    let needSnapshot = false;
    let needMarkdown = false;
    try {
      const fragmentDoc = collection.createDoc();
      const fragment = fragmentDoc.getStore();
      fragmentDoc.load();

      const rootId = fragment.addBlock('affine:page');
      fragment.addBlock('affine:surface', {}, rootId);
      const noteId = fragment.addBlock('affine:note', {}, rootId);
      const surface = getSurfaceBlock(fragment);
      if (!surface) {
        throw new Error('Failed to get surface block');
      }

      await consume(
        (function* (): Generator<void, void> {
          for (const element of selectedElements) {
            yield;
            if (element instanceof NoteBlockModel) {
              needMarkdown = true;
              for (const child of element.children) {
                yield;
                const props = yield* getBlockPropsWork(child);
                fragment.addBlock(child.flavour, props, noteId);
              }
            } else if (isAttachment(element)) {
              const { name, sourceId } = element.props;
              if (name && sourceId) {
                attachments.push({ name, sourceId });
              }
            } else if (element instanceof GfxPrimitiveElementModel) {
              needSnapshot = true;
              const props = yield* getElementPropsWork(element, new Map());
              surface.addElement(props);
            } else if (
              element instanceof EmbedSyncedDocModel ||
              element instanceof EmbedLinkedDocModel
            ) {
              const docId = element.props.pageId;
              docs.push(docId);
            }
          }
        })()
      );
      check();
      if (needSnapshot || needMarkdown) {
        const exported = await consume(transformer.docToSnapshotWork(fragment));
        check();
        if (needSnapshot) snapshot = exported ?? null;
        if (needMarkdown && exported) {
          markdown = (
            await markdownAdapter.fromDocSnapshot({
              snapshot: exported,
              assets: transformer.assetsManager,
              checkpoint,
            })
          ).file;
          check();
        }
      }
    } finally {
      try {
        transformer[Symbol.dispose]();
      } finally {
        collection.dispose();
      }
    }
  }

  return {
    snapshot: snapshot
      ? readOptions
        ? ((await prepareRequestJSON(snapshot, {
            signal: readOptions.signal,
            assertContextCurrent: readOptions.assertCurrent,
          })) ?? null)
        : JSON.stringify(snapshot)
      : null,
    combinedElementsMarkdown: markdown.length ? markdown : null,
    attachments,
    docs,
    canvasSelections: canvasSelection ? [canvasSelection] : [],
  };
}

async function extractPageSelected(
  host: EditorHost
): Promise<Partial<ChatContextValue> | null> {
  const text = await getSelectedTextContent(host, 'plain-text');
  const images = await getSelectedImagesAsBlobs(host);
  const attachments = await getSelectedAttachments(host);
  const hasText = text.length > 0;
  const hasImages = images.length > 0;

  if (hasText && !hasImages) {
    const markdown = await getSelectedTextContent(host, 'markdown');
    return {
      quote: text,
      markdown: markdown,
      attachments,
    };
  } else if (!hasText && hasImages && images.length === 1) {
    host.command
      .chain()
      .tryAll(chain => [chain.pipe(getImageSelectionsCommand)])
      .pipe(getSelectedBlocksCommand, {
        types: ['image'],
      })
      .run();
    return {
      attachments,
      images,
    };
  } else {
    const markdown =
      (await getSelectedTextContent(host, 'markdown')).trim() || '';
    return {
      quote: text,
      markdown,
      images,
      attachments,
    };
  }
}

export async function extractMarkdownFromDoc(doc: Store): Promise<string> {
  const std = new BlockStdScope({
    store: doc,
    extensions: getStoreManager().config.init().value.get('store'),
  });
  const transformer = await getTransformer(doc);
  const adapter = new MarkdownAdapter(transformer, std.provider);
  const blockModels = getNoteBlockModels(doc);
  const textModels = blockModels.filter(
    model => !matchModels(model, [ImageBlockModel, DatabaseBlockModel])
  );
  const drafts = textModels.map(toDraftModel);
  const slice = Slice.fromModels(doc, drafts);

  const snapshot = transformer.sliceToSnapshot(slice);
  if (!snapshot) {
    throw new Error('Failed to extract markdown, snapshot is undefined');
  }
  const content = await adapter.fromSliceSnapshot({
    snapshot,
    assets: transformer.assetsManager,
  });
  return content.file;
}

function getNoteBlockModels(doc: Store) {
  const notes = doc
    .getBlocksByFlavour('affine:note')
    .filter(
      note =>
        (note.model as NoteBlockModel).props.displayMode !==
        NoteDisplayMode.EdgelessOnly
    )
    .map(note => note.model as NoteBlockModel);
  const blockModels = notes.reduce((acc, note) => {
    acc.push(...note.children);
    return acc;
  }, [] as BlockModel[]);
  return blockModels;
}

async function getTransformer(doc: Store) {
  return doc.getTransformer([
    titleMiddleware(doc.workspace.meta.docMetas),
    embedSyncedDocMiddleware('content'),
  ]);
}
